import Calendar from '../base-calendar';
import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {DateRange, Props, RangeType} from '../../types';
import classNames from 'classnames';
import styles from './popup-calendar.module.scss';
import Popper from '@mui/material/Popper';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import {SVGS} from '../../../../../assets/images';
import { Lx } from '../../../../../i18n/consts';
import {Button} from '@mui/material';
import {FormattedMessage} from 'react-intl';
import {formatDateRange, isDefault, isHasRenderer, isRange} from '../../utils';
import moment from 'moment';
import {sameDay, inferRangeTypeFromValue, getPresetRange} from './helper'

const PopupCalendar: React.FC<Props> = (props) => {
    const {view, state, disablePortal, placement, allowedRangeTypes} = props;
    const range = isRange(props) && props.range;
    const onRangeComplete = range ? props.onRangeComplete : undefined;
    const value = isDefault(props) ? props.value : undefined;
    const selectedRange: DateRange | undefined = range ? props.value : value ? [value, value] : undefined;
    const renderer = isHasRenderer(props) ? props.renderer : undefined;
    const variant = props.variant ?? 'popup';
    const isSheet = variant === 'sheet';
    const showPresets = range && !props.hidePresets;

    const [open, setOpen] = useState(state?.open ?? false);
    const [firstCalendarDate, setFirstCalendarDate] = useState<moment.Moment | null>(null);
    const [previewCalendarDate, setPreviewCalendarDate] = useState<moment.Moment | undefined | null>(range && range ? undefined : null);
    const [rangeTypeState, setRangeTypeState] = useState<{ current: RangeType; previous?: RangeType }>(() => ({
        current: range
            ? inferRangeTypeFromValue(selectedRange) || RangeType.CurrentMonth
            : RangeType.Custom
    }));
    const [sheetTop, setSheetTop] = useState(0);
    const anchorRef = useRef<HTMLDivElement>(null);
    const prevOpen = React.useRef(open);
    const didAutoDefaultRef = useRef(false);

    const resetDates = () => {
        if (firstCalendarDate !== null) {
            setRangeTypeState((state) => ({
                current: state.previous ?? RangeType.Custom,
                previous: undefined
            }));
        }
        setFirstCalendarDate(null);
        setPreviewCalendarDate(null);
    };

    const close = () => {
        resetDates();
        setOpen(false);
        state?.setOpen(false);
    };

    const rangeTypes = allowedRangeTypes
        ? Object.values(RangeType).filter((value) => allowedRangeTypes.includes(value))
        : Object.values(RangeType);

    const handleToggle = () => {
        if (props.disabled) {
            return;
        }
        setOpen((prevOpen) => {
            const nextOpen = !prevOpen;
            state?.setOpen(nextOpen);
            resetDates();
            return nextOpen;
        });
    };

    const handleClose = (event?: Event | React.SyntheticEvent) => {
        event?.preventDefault();
        event?.stopPropagation();

        if (event && anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        close();
    };

    const rangeComplete = (dateRange: DateRange) => {
        if (range) {
            onRangeComplete?.(dateRange);
        }
    };

    const handleOnDayClick = (date: moment.Moment) => {
        if (props.disabled) {
            return;
        }
        if (range) {
            if (rangeTypeState.current !== RangeType.Custom) {
                setRangeTypeState((state) => ({
                    current: RangeType.Custom,
                    previous: state.current
                }));
            }
            if (firstCalendarDate == null) {
                setFirstCalendarDate(date);
                setPreviewCalendarDate(date);
            } else {
                const dateRange = formatDateRange(firstCalendarDate, date);
                setFirstCalendarDate(null);
                close();
                rangeComplete(dateRange);
            }
        } else {
            props.onCalendarDateClick?.(moment({year: date.year(), month: date.month(), day: date.date()}));
            close();
        }
    };

    const handleOnDayOverEnter = (date: moment.Moment) => {
        if (props.disabled) {
            return;
        }
        if (range) {
            if (firstCalendarDate === null) {
                return;
            }
            setPreviewCalendarDate((prevDate) => {
                if (!prevDate) {
                    return date;
                }
                const prevStamp = prevDate.unix();
                const newStamp = date.unix();
                if (prevStamp !== newStamp) {
                    return date;
                }
                return prevDate;
            });
        }
    };

    const handleOnDayOverLeave = (date: moment.Moment) => {
        if (props.disabled) {
            return;
        }
        if (!range || firstCalendarDate === null) {
            return;
        }

        if (sameDay(date, firstCalendarDate)) {
            setPreviewCalendarDate(null);
        }
    };

    const handleActionClick = (type: RangeType) => {
        if (props.disabled) {
            return;
        }
        if (!range) {
            return;
        }

        const result = getPresetRange(type);
        if (!result) {
            return;
        }

        setRangeTypeState((state) => ({
            current: type,
            previous: state.current
        }));
        rangeComplete(result);
        close();
    };

    useEffect(() => {
        if (!range) {
            return;
        }

        const hasValue = !!selectedRange?.[0] && !!selectedRange?.[1];

        if (!hasValue) {
            if (!didAutoDefaultRef.current) {
                const cm = getPresetRange(RangeType.CurrentMonth)!;
                setRangeTypeState((state) => ({ current: RangeType.CurrentMonth, previous: state.current }));
                rangeComplete(cm);
                didAutoDefaultRef.current = true;
            }
            return;
        } else {
            didAutoDefaultRef.current = true;
        }

        const inferred = inferRangeTypeFromValue(selectedRange);
        setRangeTypeState((state) => {
            if (state.current === inferred) {
                if (typeof state.previous === 'undefined') {
                    return state;
                }

                return { current: state.current };
            }

            return { current: inferred ?? RangeType.Custom };
        });

        if (previewCalendarDate) {
            setPreviewCalendarDate(null);
        }
    }, [range, selectedRange?.[0]?.valueOf(), selectedRange?.[1]?.valueOf()]);

    useEffect(() => {
        if (prevOpen.current && !open) {
            anchorRef.current?.focus();
        }

        prevOpen.current = open;
    }, [open]);

    useEffect(() => {
        if (state?.open !== undefined) {
            setOpen(state.open);
        }
    }, [state?.open]);

    useEffect(() => {
        if (!isSheet || !open) {
            return;
        }
        if (typeof window === 'undefined') {
            return;
        }

        const updatePosition = () => {
            if (!anchorRef.current) {
                return;
            }
            const rect = anchorRef.current.getBoundingClientRect();
            setSheetTop(rect.bottom);
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isSheet, open]);

    useEffect(() => {
        if (!isSheet || !open) {
            return;
        }
        if (typeof document === 'undefined') {
            return;
        }

        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previous;
        };
    }, [isSheet, open]);

    useEffect(() => {
        if (!open) {
            return;
        }
        if (typeof window === 'undefined') {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    const calendarContent = (
        <div className={classNames(styles.calendarContainer, {
            [styles.sheet]: isSheet
        })}>
            {showPresets && !isSheet && (
                <>

                    <div className={classNames(styles.actions, {
                        [styles.sheetActions]: isSheet
                    })}>
                        <h6><FormattedMessage id={Lx.General.PRESETS} /></h6>
                        {rangeTypes.map((type) => (
                            <Button
                                key={type}
                                variant={'scheduleAction'}
                                sx={{ justifyContent: 'center', textAlign: 'center' }}
                                className={classNames({
                                    [styles.calendarPresetAction]: true,
                                    [styles.active]: type === rangeTypeState.current
                                })}
                                onClick={() => handleActionClick(type)}
                                disabled={props.disabled}
                            >
                                <FormattedMessage id={type}/>
                            </Button>
                        ))}
                    </div>
                    <span className={classNames(styles.separatorLine, {
                        [styles.sheetSeparator]: isSheet
                    })}/>
                </>
            )}
            <div className={classNames(styles.calendarBody, {
                [styles.sheetBody]: isSheet
            })}>
                <Calendar
                    years={props.years}
                    view={view}
                    size={'small'}
                    onDateClick={handleOnDayClick}
                    onDayOverEnter={handleOnDayOverEnter}
                    onDayOverLeave={handleOnDayOverLeave}
                    selectedRange={selectedRange}
                    double={range && !isSheet}
                    previewRange={firstCalendarDate ? [firstCalendarDate, previewCalendarDate ?? firstCalendarDate] : undefined}
                    minDate={props.minDate}
                    maxDate={props.maxDate}
                    dense={isSheet}
                />
            </div>
            {showPresets && isSheet && (
                <>
                    <span className={classNames(styles.separatorLine, {
                        [styles.sheetSeparator]: isSheet
                    })}/>
                    <div className={classNames(styles.actions, {
                        [styles.sheetActions]: isSheet
                    })}>
                        <h6><FormattedMessage id={Lx.General.PRESETS} /></h6>
                        {rangeTypes.map((type) => (
                            <Button
                                key={type}
                                variant={'scheduleAction'}
                                sx={{ justifyContent: 'center', textAlign: 'center' }}
                                className={classNames({
                                    [styles.calendarPresetAction]: true,
                                    [styles.active]: type === rangeTypeState.current
                                })}
                                onClick={() => handleActionClick(type)}
                                disabled={props.disabled}
                            >
                                <FormattedMessage id={type}/>
                            </Button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );

    const hasDom = typeof document !== 'undefined';

    return (
        <>
            <div
                ref={anchorRef}
                id={'composition-button'}
                aria-controls={open ? 'composition-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup={'true'}
                onClick={handleToggle}
                aria-disabled={props.disabled ? 'true' : undefined}
                className={classNames({
                    [styles.actionIcon]: !renderer,
                    [styles.small]: props.small,
                    [styles.active]: open
                })}
            >
                {renderer ? renderer : <SVGS.CalendarIcon/>}
            </div>
            {!isSheet && (
                <Popper
                    disablePortal={disablePortal}
                    open={open}
                    anchorEl={anchorRef.current}
                    role={'banner'}
                    placement={placement ?? 'bottom'}
                    className={styles.popper}
                    transition
                >
                    {({ TransitionProps }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin: 'center top'
                            }}
                        >
                            <Paper className={styles.paper}>
                                <ClickAwayListener onClickAway={handleClose}>
                                    {calendarContent}
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            )}
            {isSheet && open && hasDom && createPortal(
                <div
                    className={styles.sheetOverlay}
                    style={{ top: sheetTop }}
                    onClick={() => close()}
                >
                    <div
                        className={styles.sheetSurface}
                        role={'dialog'}
                        aria-modal={'true'}
                        onClick={(event) => event.stopPropagation()}
                    >
                        {calendarContent}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default PopupCalendar;
